import { getUsername } from "@amzn/midway-cognito-type-script-provider";

import type { FeatureFlagsState } from "../store/featureFlags";
import { FeatureFlagsStore, getFlagValues } from "../store/featureFlags";
import type { UserPreferenceList } from "../store/userPreferences";
import {
    UserPreference,
    UserPreferenceData,
    UserPreferencesStore,
} from "../store/userPreferences";

import LocalStorageManager from "./localStorage.utils";

// Static key for where we will store user preferences in local storage
// V2: Key changed to reset all users' cached preferences after fixing the
// boot-sequence bug that silently wrote "false" for flag-controlled prefs
// before Aquila had been consulted. See: userPreferences-bug-analysis.md
const USER_PREF_LOCAL_STORAGE_KEY = "UserPreferencesV2";

/**
 * Retrieves the saved user preferences from storage
 * TODO: This will be migrated to an API call later, so we are marking it as async for now
 * @returns The user's current preferences
 */
export const loadUserPreferences = async (): Promise<UserPreferenceList> => {
    const savedPrefs =
        (LocalStorageManager.getWithExpiry(
            USER_PREF_LOCAL_STORAGE_KEY,
        ) as UserPreferenceList) ?? {};

    // Fill in any blanks we may have from newly added features since the user last saved.
    // IMPORTANT: For preferences that are controlled by a feature flag, we intentionally
    // leave them undefined if the user has not explicitly opted in/out. This ensures
    // that the precedence logic in isFeatureEnabledWithPrecedence() can fall through
    // to Aquila (precedence #3) instead of being short-circuited by a synthesised "false".
    Object.keys(UserPreference).forEach((preference) => {
        const key = preference as UserPreference;
        const preferenceValue = savedPrefs[key];
        const preferenceData = UserPreferenceData[preference];

        if (preferenceValue !== undefined) {
            // User has an explicit saved value -- keep it as-is
            return;
        }

        // If there is no value, and we have a feature flag, grab the value from our feature flag store
        if (preferenceData.featureFlagControl !== undefined) {
            const featureFlagValue =
                FeatureFlagsStore.getState()[
                    preferenceData.featureFlagControl as keyof FeatureFlagsState
                ];
            if (featureFlagValue !== undefined) {
                savedPrefs[key] = featureFlagValue.toString();
            }
            return;
        }

        // In all other case we must return nothing or wather we return will
        // be stored as chosen option.
    });

    return savedPrefs;
};

/**
 * Saves the users current preferences to storage
 */
export const saveUserPreferences = async (): Promise<void> => {
    const preferences = UserPreferencesStore.getState().getAllPreferences();
    LocalStorageManager.storeWithExpiry(
        USER_PREF_LOCAL_STORAGE_KEY,
        preferences,
        365,
    );

    // Refresh the feature flag store
    const flags = await getFlagValues(await getUsername());
    FeatureFlagsStore.getState().setFeatureFlags(flags);
};
